'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { Zap, Sparkles, Clock, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { TransformationGallery } from '@/components/transformation-gallery';

export default function DetailingPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const packages = [
    {
      name: "Express",
      price: "$89",
      duration: "1.5 hours",
      color: "from-orange-400 to-orange-600",
      features: [
        "Interior vacuum & wipe down",
        "Exterior wash",
        "Tire shine",
        "Glass cleaning",
        "Air freshener"
      ]
    },
    {
      name: "Basic",
      price: "$199",
      duration: "3 hours",
      color: "from-amber-400 to-amber-600",
      popular: true,
      features: [
        "Express package +",
        "Clay bar treatment",
        "Wax application",
        "Engine bay cleaning",
        "Trim restoration",
        "Carpet shampooing",
        "Leather conditioning"
      ]
    },
    {
      name: "Premium",
      price: "$349",
      duration: "4 hours",
      color: "from-red-400 to-red-600",
      features: [
        "Basic package +",
        "Paint sealant (6 months)",
        "Advanced stain removal",
        "Headlight restoration",
        "Premium ceramic spray",
        "Odor elimination treatment",
        "Interior leather full restoration"
      ]
    }
  ];

  return (
    <>
      <Navbar data-zylo-id="zylo-4ea218a51989" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="83:7-83:17" data-zylo-component="DetailingPage" />

      {/* Hero Section */}
      <section data-zylo-id="zylo-1781461765c1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="86:7-128:17" data-zylo-component="DetailingPage" className="relative overflow-hidden bg-gradient-to-b from-orange-50 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-36bed000bd6a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="87:9-127:15" data-zylo-component="DetailingPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-c57dd16165ad" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="88:11-126:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div data-zylo-id="zylo-d3164d732540" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="94:13-99:26" data-zylo-component="DetailingPage" variants={itemVariants} className="mb-6 inline-block">
              <Image
                src="/images/generated/professional-banner-blue-kraken.png"
                alt="Blue Kraken Auto Detailing Banner"
                width={800}
                height={450}
                className="w-full max-w-2xl h-auto"
                priority
              />
            </motion.div>

            <motion.h1 data-zylo-id="zylo-449ff187cf9e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="101:13-106:25" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl text-foreground"
            >
              Showroom Shine, Every Time
            </motion.h1>

            <motion.p data-zylo-id="zylo-67feeeac2d86" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="108:13-113:24" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="mt-6 text-lg text-muted-foreground sm:text-xl"
            >
              Professional auto detailing at your location. From quick Express touch-ups to full Premium transformations, we bring your car back to life.
            </motion.p>

            <motion.div data-zylo-id="zylo-44f6bf248631" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="115:13-125:26" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button data-zylo-id="zylo-52c9dcc9fde4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="119:15-121:24" data-zylo-component="DetailingPage" size="lg" asChild className="bg-accent hover:bg-accent/90">
                <Link data-zylo-id="zylo-719b10773cf5" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="120:17-120:61" data-zylo-component="DetailingPage" href="/detailing/book">Book Now</Link>
              </Button>
              <Button data-zylo-id="zylo-4a9e463d1f36" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="122:15-124:24" data-zylo-component="DetailingPage" size="lg" variant="outline" asChild>
                <Link data-zylo-id="zylo-44a24e399ed7" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="123:17-123:60" data-zylo-component="DetailingPage" href="#packages">View Packages</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Our Detailing */}
      <section data-zylo-id="zylo-dee88ef8ff75" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="131:7-177:17" data-zylo-component="DetailingPage" className="bg-accent/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-9558dba06559" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="132:9-176:15" data-zylo-component="DetailingPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-a783461e2ae2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="133:11-175:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                icon: Sparkles,
                title: "Premium Results",
                description: "Professional-grade products and techniques"
              },
              {
                icon: MapPin,
                title: "We Come To You",
                description: "Service at your home or location"
              },
              {
                icon: Clock,
                title: "Quick & Convenient",
                description: "Efficient service without compromising quality"
              },
              {
                icon: Zap,
                title: "Satisfaction Guaranteed",
                description: "100% happiness guarantee on all services"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div data-zylo-id="zylo-a15224905414" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="164:17-172:30" data-zylo-component="DetailingPage" key={index} variants={itemVariants}>
                  <Card data-zylo-id="zylo-79027a194e85" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="165:19-171:26" data-zylo-component="DetailingPage" className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent data-zylo-id="zylo-6a05e1e20ed8" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="166:21-170:35" data-zylo-component="DetailingPage" className="p-6">
                      <Icon data-zylo-id="zylo-289d8841ee8f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="167:23-167:70" data-zylo-component="DetailingPage" className="h-12 w-12 text-accent mb-4" />
                      <h3 data-zylo-id="zylo-f3143183c321" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="168:23-168:79" data-zylo-component="DetailingPage" className="text-lg font-bold mb-2">{item.title}</h3>
                      <p data-zylo-id="zylo-b0f5bc00f19b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="169:23-169:90" data-zylo-component="DetailingPage" className="text-sm text-muted-foreground">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Transformation Gallery */}
      <section data-zylo-id="zylo-1230f9628ed7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="180:7-212:17" data-zylo-component="DetailingPage" className="py-16 sm:py-24">
        <div data-zylo-id="zylo-d557e1addd2b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="181:9-211:15" data-zylo-component="DetailingPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-a2d0974194c2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="182:11-201:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl text-center mb-12"
          >
            <motion.h2 data-zylo-id="zylo-9f1b2b229714" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="189:13-194:25" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl text-foreground mb-4"
            >
              Before & After Transformations
            </motion.h2>
            <motion.p data-zylo-id="zylo-b73d390cc8ab" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="195:13-200:24" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              See the dramatic difference our detailing makes
            </motion.p>
          </motion.div>

          <motion.div data-zylo-id="zylo-695215ddfe7c" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="203:11-210:24" data-zylo-component="DetailingPage"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <TransformationGallery data-zylo-id="zylo-c68aca052b30" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="209:13-209:38" data-zylo-component="DetailingPage" />
          </motion.div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section data-zylo-id="zylo-f0a36f66f511" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="215:7-276:17" data-zylo-component="DetailingPage" id="packages" className="bg-gradient-to-b from-white to-accent/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-ed81766e3a62" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="216:9-275:15" data-zylo-component="DetailingPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-43d72702fcb6" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="217:11-236:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl text-center mb-12"
          >
            <motion.h2 data-zylo-id="zylo-511e86fce501" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="224:13-229:25" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl text-foreground mb-4"
            >
              Detailing Packages
            </motion.h2>
            <motion.p data-zylo-id="zylo-8bcce0c4a68b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="230:13-235:24" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Choose the perfect package for your vehicle
            </motion.p>
          </motion.div>

          <motion.div data-zylo-id="zylo-5f333dbc0d8b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="238:11-274:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {packages.map((pkg, index) => (
              <motion.div data-zylo-id="zylo-b02ffc75787b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="246:15-272:28" data-zylo-component="DetailingPage" key={index} variants={itemVariants}>
                <Card data-zylo-id="zylo-05f3e609f716" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="247:17-271:24" data-zylo-component="DetailingPage" className={`h-full border-0 shadow-lg hover:shadow-2xl transition-all ${pkg.popular ? 'ring-2 ring-accent md:scale-105' : ''}`}>
                  {pkg.popular && (
                    <div data-zylo-id="zylo-b833c9007de3" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="249:21-251:27" data-zylo-component="DetailingPage" className="bg-accent text-white px-4 py-2 text-sm font-bold text-center">
                      MOST POPULAR
                    </div>
                  )}
                  <CardHeader data-zylo-id="zylo-6abb4d17b8f9" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="253:19-257:32" data-zylo-component="DetailingPage" className="bg-gradient-to-r text-white rounded-t-lg" style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }}>
                    <CardTitle data-zylo-id="zylo-7c8380d00b89" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="254:21-254:75" data-zylo-component="DetailingPage" className="text-2xl">{pkg.name}</CardTitle>
                    <div data-zylo-id="zylo-c9d644380663" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="255:21-255:79" data-zylo-component="DetailingPage" className="text-3xl font-bold mt-2">{pkg.price}</div>
                    <div data-zylo-id="zylo-e05d9cb6caf1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="256:21-256:77" data-zylo-component="DetailingPage" className="text-sm opacity-90">{pkg.duration}</div>
                  </CardHeader>
                  <CardContent data-zylo-id="zylo-77dcbdc62542" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="258:19-270:33" data-zylo-component="DetailingPage" className="p-6">
                    <ul data-zylo-id="zylo-95a118ac80a4" data-zylo-kind="list" data-zylo-cap="59" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="259:21-266:26" data-zylo-component="DetailingPage" className="space-y-3 mb-6">
                      {pkg.features.map((feature, i) => (
                        <li data-zylo-id="zylo-e33bb4d9ebb9" data-zylo-kind="list" data-zylo-cap="59" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="261:25-264:30" data-zylo-component="DetailingPage" key={i} className="flex items-start gap-3">
                          <span data-zylo-id="zylo-e474a1b76e1e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="262:27-262:83" data-zylo-component="DetailingPage" className="text-accent font-bold text-lg">✓</span>
                          <span data-zylo-id="zylo-338138c14d16" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="263:27-263:85" data-zylo-component="DetailingPage" className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button data-zylo-id="zylo-e81fd7b9ed23" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="267:21-269:30" data-zylo-component="DetailingPage" asChild className="w-full" variant={pkg.popular ? "default" : "outline"}>
                      <Link data-zylo-id="zylo-9ddfb3a932b0" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="268:23-268:74" data-zylo-component="DetailingPage" href="/detailing/book">Book {pkg.name}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section data-zylo-id="zylo-1c4d2ff0c782" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="279:7-310:17" data-zylo-component="DetailingPage" className="bg-gradient-to-r from-accent to-orange-600 py-16 sm:py-24 text-white">
        <div data-zylo-id="zylo-970d0f8004c2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="280:9-309:15" data-zylo-component="DetailingPage" className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div data-zylo-id="zylo-77d0b8f7e14a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="281:11-308:24" data-zylo-component="DetailingPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 data-zylo-id="zylo-ba8b2054809f" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="287:13-292:25" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl mb-4"
            >
              Ready for Your Detailing?
            </motion.h2>
            <motion.p data-zylo-id="zylo-1ce9202f6e73" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="293:13-298:24" data-zylo-component="DetailingPage"
              variants={itemVariants}
              className="text-lg text-white/90 mb-8"
            >
              Book your appointment today and enjoy a $50 deposit to secure your spot.
            </motion.p>
            <motion.div data-zylo-id="zylo-6ac20319f834" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="299:13-307:26" data-zylo-component="DetailingPage" variants={itemVariants}>
              <Button data-zylo-id="zylo-27bf57acf643" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="300:15-306:24" data-zylo-component="DetailingPage"
                size="lg"
                asChild
                className="bg-white text-accent hover:bg-gray-100"
              >
                <Link data-zylo-id="zylo-f71afd24ed20" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="305:17-305:72" data-zylo-component="DetailingPage" href="/detailing/book">Book Your Detailing</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-01977f75b999" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/detailing/page.tsx" data-zylo-loc="312:7-312:17" data-zylo-component="DetailingPage" />
    </>
  );
}
